import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from 'react-query'
import { useNavigate } from 'react-router-dom'
import apiService from '../services/api'
import AdminLayout from '../components/AdminLayout'
import { RefreshCw, Eye, CheckCircle, Trash2, Clock } from 'lucide-react'
import { formatDate } from '../utils/dateUtils'

const AdminOrders = () => {
  const [page] = useState(1)
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [selectedReservation, setSelectedReservation] = useState(null)

  const { data, isLoading } = useQuery(
    ['admin-orders', page],
    () => apiService.get('/reservations', { params: { page, limit: 50 } }).then(r => r.data)
  )
  const reservations = data?.reservations || data?.rows || []

  const updateStatusMutation = useMutation(
    ({ id, status }) => apiService.updateReservationStatus(id, status),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('admin-orders')
      }
    }
  )

  const deleteMutation = useMutation(
    (id) => apiService.deleteReservation(id),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('admin-orders')
        setShowDeleteModal(false)
        setSelectedReservation(null)
      }
    }
  )

  const getStatusColor = (status) => {
    switch (status) {
      case 'confirmed':
        return 'text-green-600 bg-green-100'
      case 'demande_envoyee':
        return 'text-orange-600 bg-orange-100'
      case 'cancelled':
        return 'text-red-600 bg-red-100'
      default:
        return 'text-gray-400 bg-gray-100'
    }
  }

  const getStatusIcon = (status) => {
    switch (status) {
      case 'confirmed':
        return <CheckCircle className="h-4 w-4" />
      case 'demande_envoyee':
        return <Clock className="h-4 w-4" />
      case 'cancelled':
        return <Trash2 className="h-4 w-4" />
      default:
        return <Clock className="h-4 w-4" />
    }
  }

  const getStatusLabel = (status) => {
    switch (status) {
      case 'confirmed':
        return 'Confirmée'
      case 'demande_envoyee':
        return 'Demande envoyée'
      case 'cancelled':
        return 'Annulée'
      case 'completed':
        return 'Terminée'
      default:
        return status
    }
  }

  const formatPrice = (price) => {
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'EUR'
    }).format(price)
  }


  const handleStatusUpdate = (id, status) => {
    updateStatusMutation.mutate({ id, status })
  }

  const handleDelete = (id) => {
    setSelectedReservation(id)
    setShowDeleteModal(true)
  }

  const confirmDelete = () => {
    if (selectedReservation) {
      deleteMutation.mutate(selectedReservation)
    }
  }

  return (
    <AdminLayout title="Administration" subtitle="Commandes">
      <div className="card">
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <h3 className="text-lg font-semibold text-gray-900">Liste des réservations</h3>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="text-left py-3 px-4 font-semibold text-gray-700">ID commande</th>
                <th className="text-left py-3 px-4 font-semibold text-gray-700">Client</th>
                <th className="text-left py-3 px-4 font-semibold text-gray-700">Période</th>
                <th className="text-left py-3 px-4 font-semibold text-gray-700">Total</th>
                <th className="text-left py-3 px-4 font-semibold text-gray-700">Statut</th>
                <th className="text-left py-3 px-4 font-semibold text-gray-700">Actions</th>
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                <tr>
                  <td colSpan="6" className="py-12 text-center">
                    <div className="flex flex-col items-center">
                      <RefreshCw className="h-8 w-8 animate-spin text-orange-500 mb-4" />
                      <p className="text-gray-600">Chargement des réservations...</p>
                    </div>
                  </td>
                </tr>
              ) : (
                reservations.map((reservation) => (
                  <tr key={reservation.id} className="border-b border-gray-200 hover:bg-gray-50">
                    <td className="py-4 px-4">
                      <span className="font-semibold">#{reservation.id}</span>
                    </td>
                    <td className="py-4 px-4">
                      <div>
                        <p className="font-medium">{reservation.first_name} {reservation.last_name}</p>
                        <p className="text-sm text-gray-600">{reservation.email}</p>
                      </div>
                    </td>
                    <td className="py-4 px-4">
                      <p className="text-sm">{formatDate(reservation.start_date)}</p>
                      <p className="text-sm text-gray-600">au {formatDate(reservation.end_date)}</p>
                    </td>
                    <td className="py-4 px-4">
                      <p className="font-medium">{formatPrice(reservation.total_price)}</p>
                    </td>
                    <td className="py-4 px-4">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(reservation.status)}`}>
                        {getStatusIcon(reservation.status)}
                        <span className="ml-1">{getStatusLabel(reservation.status)}</span>
                      </span>
                    </td>
                    <td className="py-4 px-4">
                      <div className="flex items-center space-x-2">
                        <button
                          onClick={() => navigate(`/admin/reservations/${reservation.id}`, { state: { reservation } })}
                          className="text-blue-600 hover:text-blue-800"
                        >
                          <Eye className="h-4 w-4" />
                        </button>
                        {reservation.status === 'demande_envoyee' && (
                          <button
                            onClick={() => handleStatusUpdate(reservation.id, 'confirmed')}
                            className="text-green-600 hover:text-green-800"
                            disabled={updateStatusMutation.isLoading}
                          >
                            <CheckCircle className="h-4 w-4" />
                          </button>
                        )}
                        <button
                          onClick={() => handleDelete(reservation.id)}
                          className="text-red-600 hover:text-red-800"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {reservations.length === 0 && !isLoading && (
        <div className="text-center py-12">
          <p className="text-gray-600">Aucune réservation trouvée</p>
        </div>
      )}

      {showDeleteModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Confirmer la suppression</h3>
            <p className="text-gray-600 mb-6">
              Êtes-vous sûr de vouloir supprimer cette réservation ? Cette action est irréversible.
            </p>
            <div className="flex justify-end space-x-4">
              <button
                onClick={() => setShowDeleteModal(false)}
                className="px-4 py-2 text-gray-600 hover:text-gray-800"
              >
                Annuler
              </button>
              <button
                onClick={confirmDelete}
                className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
                disabled={deleteMutation.isLoading}
              >
                {deleteMutation.isLoading ? 'Suppression...' : 'Supprimer'}
              </button>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  )
}

export default AdminOrders


